import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const HeroSection = () => {
  const { t, positioning } = useLanguage();

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black"
      data-testid="hero-section"
    >
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 dot-pattern opacity-30" />
      
      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black opacity-60" />
      
      {/* Decorative lines */}
      <div className="absolute left-12 top-1/3 w-px h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent hidden lg:block" />
      <div className="absolute right-12 bottom-1/3 w-px h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent hidden lg:block" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
        <div className="max-w-4xl mx-auto text-center">
          {/* Overline */}
          <div 
            className="flex items-center justify-center gap-4 mb-8 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}
          >
            <div className="w-12 h-px bg-white/20" />
            <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-zinc-500" data-testid="hero-overline">
              {t.hero.overline}
            </p>
            <div className="w-12 h-px bg-white/20" />
          </div>

          {/* Main Headline */}
          <h1 
            className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight leading-[1.15] text-white mb-8 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
            data-testid="hero-headline"
          >
            {t.hero[positioning.heroMessage]}
          </h1>

          {/* Subtitle */}
          <p 
            className="text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl mx-auto mb-14 font-light opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          {/* CTA Buttons */}
          <div 
            className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
          >
            <button 
              onClick={scrollToContact}
              className="btn-primary flex items-center justify-center gap-3 group"
              data-testid="hero-cta-primary"
            >
              <span>{t.hero.cta1}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button 
              onClick={scrollToContact}
              className="btn-secondary flex items-center justify-center"
              data-testid="hero-cta-secondary"
            >
              {t.hero.cta2}
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 opacity-0 animate-fade-in" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent animate-bounce-subtle" />
      </div>
    </section>
  );
};

export default HeroSection;
